import React from "react";
import { useTranslation } from "react-i18next";
import { formatLongDate, formatSlotRange, zoneLabel } from "./book-tour-format";

function CopyJoinLink({ url }) {
  const { t } = useTranslation("book-tour");
  const [copied, setCopied] = React.useState(false);
  const timerRef = React.useRef(null);

  React.useEffect(() => () => clearTimeout(timerRef.current), []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = url;
      ta.setAttribute("readonly", "");
      ta.style.position = "absolute";
      ta.style.left = "-9999px";
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
      } catch {
        /* clipboard unavailable */
      }
      document.body.removeChild(ta);
    }
    setCopied(true);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setCopied(false), 1600);
  };

  return (
    <button
      type="button"
      className={`book-tour-copy-link${copied ? " is-copied" : ""}`}
      onClick={copy}
      title={t("confirmation.clickToCopy")}
    >
      <span className="book-tour-copy-link-url">{url}</span>
      <span className="book-tour-copy-link-action" aria-hidden="true">
        {copied ? t("confirmation.copied") : t("confirmation.clickToCopy")}
      </span>
    </button>
  );
}

/**
 * Post-booking screen. Repeats the details in the gym's timezone and offers the
 * same .ics the confirmation email carries, built client-side so the page needs
 * no extra round trip.
 */
function buildIcsDataUrl({ booking, config }) {
  const esc = (v) =>
    String(v ?? "")
      .replace(/\\/g, "\\\\")
      .replace(/;/g, "\\;")
      .replace(/,/g, "\\,")
      .replace(/\r?\n/g, "\\n");
  const stamp = (iso) =>
    `${new Date(iso).toISOString().replace(/[-:]/g, "").split(".")[0]}Z`;
  const providerKey = booking.meeting_provider || config.meeting_provider;
  const providerLabel =
    providerKey === "google_meet"
      ? "Google Meet"
      : providerKey === "zoom"
        ? "Zoom"
        : null;
  const online =
    config.location_type === "online" || Boolean(booking.meeting_join_url);
  const locationText = online
    ? `Online${providerLabel ? ` — ${providerLabel}` : ""}`
    : config.location || config.gym?.name || "";
  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//MyStrengthBook//Consult//EN",
    "BEGIN:VEVENT",
    `UID:${esc(booking.booking_number)}@mystrengthbook.com`,
    `DTSTAMP:${stamp(new Date().toISOString())}`,
    `DTSTART:${stamp(booking.start_datetime)}`,
    `DTEND:${stamp(booking.end_datetime)}`,
    `SUMMARY:${esc(`${booking.tour_name} — ${config.gym?.name ?? ""}`)}`,
    `LOCATION:${esc(locationText)}`,
    ...(booking.meeting_join_url
      ? [
          `URL:${esc(booking.meeting_join_url)}`,
          `DESCRIPTION:${esc(`Join: ${booking.meeting_join_url}`)}`,
        ]
      : []),
    "STATUS:CONFIRMED",
    "END:VEVENT",
    "END:VCALENDAR",
  ];
  return `data:text/calendar;charset=utf-8,${encodeURIComponent(lines.join("\r\n"))}`;
}

const BookTourConfirmation = ({
  booking,
  config,
  displayTz,
  onBookAnother,
}) => {
  const { t } = useTranslation("book-tour");
  const tz = displayTz || booking.timezone || config.timezone;

  const providerKey = booking.meeting_provider || config.meeting_provider;
  const providerLabel =
    providerKey === "google_meet"
      ? "Google Meet"
      : providerKey === "zoom"
        ? "Zoom"
        : null;
  const isOnline =
    config.location_type === "online" || Boolean(booking.meeting_join_url);

  return (
    <section className="book-tour-confirmation" aria-live="polite">
      <div className="book-tour-confirmation-mark" aria-hidden>
        ✓
      </div>
      <h2 className="book-tour-confirmation-title">
        {t("confirmation.title")}
      </h2>
      <p className="book-tour-confirmation-lede">
        {t("confirmation.emailed", {
          email: booking.guest_contact?.email ?? "",
        })}
      </p>

      <dl className="book-tour-summary">
        <dt>{t("confirmation.what")}</dt>
        <dd>{booking.tour_name}</dd>
        <dt>{t("confirmation.when")}</dt>
        <dd>
          {formatLongDate(booking.start_datetime, tz)}
          <br />
          {formatSlotRange(booking.start_datetime, booking.end_datetime, tz)} (
          {zoneLabel(booking.start_datetime, tz)})
        </dd>
        {(isOnline || config.location || config.gym?.name) && (
          <>
            <dt>{t("confirmation.where")}</dt>
            <dd>
              {isOnline
                ? `${t("meta.online")}${providerLabel ? ` — ${providerLabel}` : ""}`
                : config.location || config.gym?.name}
            </dd>
          </>
        )}
        {booking.meeting_join_url ? (
          <>
            <dt className="place-content-center">{t("confirmation.join")}</dt>
            <dd>
              <CopyJoinLink url={booking.meeting_join_url} />
            </dd>
          </>
        ) : isOnline ? (
          <>
            <dt>{t("confirmation.join")}</dt>
            <dd>{t("confirmation.joinPending")}</dd>
          </>
        ) : null}
        <dt>{t("confirmation.reference")}</dt>
        <dd>{booking.booking_number}</dd>
      </dl>

      <div className="book-tour-actions">
        <a
          className="book-tour-btn-secondary"
          href={buildIcsDataUrl({ booking, config })}
          download="consult.ics"
        >
          {t("confirmation.addToCalendar")}
        </a>
        <button
          type="button"
          className="book-tour-btn-primary"
          onClick={onBookAnother}
        >
          {t("confirmation.bookAnother")}
        </button>
      </div>
    </section>
  );
};

export default BookTourConfirmation;
